import * as ko from "knockout";
import {
    IKnockoutComponent,
    IRouteDefinition,
    IRouteDefinitionParams,
    TRouteId
} from "../../index";
import {getRouteDefinition} from "../../constants/RouteDefinitions";
import {isShellContentChildRoute, navigateToShellTab} from "../../util/pageShellTabRoutes";
import {TabManager} from "../tab-list/TabManager";
import {resolveShellTabId} from "../../util/pageShellTabMemory";
import {TComponentName} from "../../constants/ComponentRegistry";

/**
 * Tab orchestration for PageShell routes: URL-driven tab selection and direct tab component mounting.
 */
export class PageController extends TabManager {
    override readonly isPageShell: boolean = true;
    /** When true, accepting a URL tab must not write tab memory (availability fallback). */
    private suppressTabMemoryWrite = false;
    /** Non-tab child route whose component fills the body while its tabDefault stays highlighted. */
    private readonly contentChildDef: IRouteDefinition | null =
        this.__tabRouteDef && isShellContentChildRoute(this.__routeDef, this.__tabRouteDef)
            ? this.__tabRouteDef
            : null;

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.syncCurrentIdFromUrl();
        this.updateComponent();
    }

    /** True when the shell owns tab UI via a single content wrapper component. */
    private hasContentWrapper(): boolean {
        return 'content' in this.__routeDef && !!this.__routeDef.content;
    }

    /** replaceHash to a tab without treating it as a user memory write. */
    private replaceToTab(tabId: TRouteId) {
        this.suppressTabMemoryWrite = true;
        try {
            navigateToShellTab(this.__routeDef.id, tabId, this.__routeParams ?? {}, true);
        } finally {
            this.suppressTabMemoryWrite = false;
        }
    }

    /** Soft-nav / URL sync — skip persisting when this navigation is an availability fallback. */
    public override setTabId(tabId: TRouteId) {
        if (this.suppressTabMemoryWrite) {
            this.selectTabWithoutPersisting(tabId);
            return;
        }
        super.setTabId(tabId);
    }

    /** Read active tab from URL; persist only when the URL tab is available. */
    private syncCurrentIdFromUrl() {
        if (this.contentChildDef) {
            // Child route fills the body; highlight its tabDefault without rewriting the child URL.
            const highlightId = this.contentChildDef.tabDefault;
            if (highlightId && this.getTab(highlightId)) {
                this.setTabId(highlightId);
            }
            return;
        }
        const shellId = (this.__shellRouteId ?? this.__routeDef.id) as TRouteId;
        const tabRouteDef = this.__tabRouteDef;
        const explicitTabId = (tabRouteDef?.id
            ?? (this.__routeId !== shellId ? this.__routeId : null)) as TRouteId | null;

        const isAvailable = (tabId: TRouteId) => {
            const tab = this.getTab(tabId);
            return !!tab && this.isTabAvailable(tab);
        };

        if (explicitTabId && isAvailable(explicitTabId)) {
            this.selectTabAndPersist(explicitTabId);
            return;
        }

        const resolved = resolveShellTabId(this.__routeDef, {
            explicitTabId,
            storedTabId: this.tabMemory(),
            isAvailable,
        });
        if (!resolved) {
            return;
        }

        this.currentId(resolved);
        if (explicitTabId && explicitTabId !== resolved) {
            // Forbidden / unavailable deep link — replace URL, do not persist fallback over preference.
            this.replaceToTab(resolved);
        }
    }

    protected override updateTabsOnVisibilityChange() {
        if (this.contentChildDef) {
            // Body is the child component (not tab-driven); availability must not rewrite the child URL.
            return;
        }
        cancelAnimationFrame(this._reqUpdateTabsOnVisibilityChange);
        this._reqUpdateTabsOnVisibilityChange = requestAnimationFrame(() => {
            const current = this.currentTab();
            if (current && this.isTabAvailable(current)) {
                return;
            }
            const fallbackId = this.ensureCurrentTabAvailable();
            if (fallbackId) {
                this.replaceToTab(fallbackId);
            }
        });
    }

    /** Mount the shell content wrapper, or the active tab component when the shell has no content. */
    protected override updateComponent() {
        if (this.contentChildDef) {
            this.mountContentChild(this.contentChildDef);
            return;
        }
        if (this.hasContentWrapper()) {
            this.mountContentWrapper();
            return;
        }
        const tab = this.currentTab();
        if (!tab) {
            this.currentComponent(null);
            return;
        }
        const tabRouteDef = getRouteDefinition(tab.id);
        if (!tabRouteDef?.component?.name) {
            this.currentComponent(null);
            return;
        }
        // Tabs with a different fetch (e.g. hierarchy org-chart) must not reuse the shell getobject.
        const shellService = this.__routeDef.fetchRouteDef?.serviceName;
        const tabService = tabRouteDef.fetchRouteDef?.serviceName;
        const shareShellRequest = !tabService || tabService === shellService;
        const component: IKnockoutComponent = {
            name: tabRouteDef.component.name,
            params: {
                __routeDef: this.getDef(tab.id),
                __routeId: tab.id,
                ...(shareShellRequest && this.__requestRouteData
                    ? {__requestRouteData: this.__requestRouteData}
                    : {}),
                __routeParams: this.__routeParams,
                __tab: tab,
                __tabManager: this,
                __id: this.__id,
                ...(tabRouteDef.component.params ?? {}),
            },
        };
        const prev = this.currentComponent();
        if (prev?.name === component.name && prev?.params?.__routeId === component.params.__routeId) {
            return;
        }
        this.currentComponent(component as IKnockoutComponent);
    }

    /** Mount a non-tab child route's component (e.g. edit permissions) as the shell body. */
    private mountContentChild(childDef: IRouteDefinition) {
        const name = childDef.component?.name;
        if (!name) {
            this.currentComponent(null);
            return;
        }
        const prev = this.currentComponent();
        if (prev?.name === name && prev.params?.__routeId === childDef.id) {
            return;
        }
        // Children with a different fetch must not reuse the shell getobject (same rule as tabs).
        const shellService = this.__routeDef.fetchRouteDef?.serviceName;
        const childService = childDef.fetchRouteDef?.serviceName;
        const shareShellRequest = !childService || childService === shellService;
        this.currentComponent({
            name,
            params: {
                __routeDef: childDef,
                __routeId: childDef.id,
                ...(shareShellRequest && this.__requestRouteData
                    ? {__requestRouteData: this.__requestRouteData}
                    : {}),
                __routeParams: this.__routeParams,
                __tab: this.currentTab(),
                __tabManager: this,
                __id: this.__id,
                ...(childDef.component?.params ?? {}),
            },
        } as IKnockoutComponent);
    }

    private mountContentWrapper() {
        let name: TComponentName;
        if (this.__routeDef.content === true) {
            name = 'component/route/details/basic';
        } else {
            name = this.__routeDef.content as TComponentName;
        }
        const def = getRouteDefinition(this.__routeDef);
        if (!def) {
            return;
        }
        const prev = this.currentComponent();
        // Same content component for every tab — never replace (preserves form viewModels).
        if (prev?.name === name) {
            return;
        }
        this.currentComponent({
            name,
            params: {
                __routeDef: def,
                __routeId: def.id,
                __requestRouteData: this.__requestRouteData,
                __routeParams: this.__routeParams,
                __tab: this.currentTab(),
                __tabManager: this,
                __id: this.__id,
            },
        } as IKnockoutComponent);
    }

    public override onClick(id: TRouteId) {
        const tab = this.getTab(id);
        if (!tab || !this.isTabAvailable(tab)) {
            return;
        }
        if (this.tabClickInterceptor && !this.tabClickInterceptor(id)) {
            return;
        }
        this.selectTabAndPersist(id);
        navigateToShellTab(this.__routeDef.id, id, this.__routeParams ?? {});
    }

    /** After object-driven show() updates — keep URL aligned without clobbering memory. */
    public onTabsAvailabilityChanged() {
        if (this.contentChildDef) {
            return;
        }
        const previousId = this.currentId();
        const fallbackId = this.ensureCurrentTabAvailable();
        if (fallbackId && fallbackId !== previousId) {
            this.replaceToTab(fallbackId);
        }
    }
}
